import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import { EditOutlined } from "@ant-design/icons";
import { ProColumns, ProTable, ActionType } from "@ant-design/pro-components";
import { App, Button } from "antd";

import dayjs from "../../../config/dayjs";
import { departmentTypeLabels } from "../../../constant/enum";
import { getUser } from "../../../services/auth";
import { removeEmptyParams } from "../../../utils/listQueryHelper";
import { waitTime } from "../../../utils/WaitTime";
import {
  downloadPayslipFileFromS3,
  getPayslips,
  getUserPayslips,
} from "../api/payslipAPI";
import { IPayslipResponse } from "../type/payslip";

export const PayslipTable = () => {
  const navigate = useNavigate();
  const actionRef = useRef<ActionType>();
  const { message } = App.useApp();
  const user = getUser();
  const role = user.role;

  const columns: ProColumns<IPayslipResponse>[] = [
    {
      title: "No",
      dataIndex: "id",
      key: "id",
      search: false,
      align: "center" as const,
      render: (_, __, index) => <span>{index + 1}</span>,
    },
    {
      title: "Date",
      dataIndex: "payslipDate",
      key: "payslipDate",
      valueType: "dateMonth",
      align: "center" as const,
      width: 150,
      render: (_, record) => {
        const date = dayjs(record.payslipDate).format("MMMM YYYY");
        return date;
      },
      fieldProps: {
        format: "MMMM YYYY",
        picker: "month",
      },
      search: {
        transform: (value) => {
          if (!value) return {};
          const dateObj = dayjs.isDayjs(value)
            ? value
            : dayjs(value, "MMMM YYYY", true);
          if (!dateObj.isValid()) {
            console.error("Invalid date:", value);
            return {};
          }

          const parsed = dateObj.format("YYYY-MM-01");
          return { createdAt: parsed };
        },
      },
    },
    ...(role === "admin" || role === "branchAdmin"
      ? [
          {
            title: "User",
            dataIndex: "user",
            key: "user",
            search: role === "admin" || role === "branchAdmin" ? true : false,
            render: (_: any, record: IPayslipResponse) => (
              <span>{record.user.name}</span>
            ),
          },
          {
            title: "Employee Number",
            dataIndex: ["user", "employeeNumber"],
            key: "user.employeeNumber",
            align: "center" as const,
            sorter: true,
          },
          {
            title: "Department",
            dataIndex: "department",
            key: "department",
            valueType: "select" as const,
            valueEnum: departmentTypeLabels,
            align: "center" as const,
            render: (_: any, record: IPayslipResponse) => {
              const department = record.department;
              const departmentLabel =
                departmentTypeLabels[
                  department as keyof typeof departmentTypeLabels
                ] ?? department;

              return <span>{departmentLabel}</span>;
            },
            fieldProps: {
              showSearch: true,
              placeholder: "Search department",
              filterOption: (
                input: string,
                option?: { label: string; value: string }
              ) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase()),
            },
            search: {
              transform: (value: string) => ({ department: value }),
            },
          },
        ]
      : []),
    {
      title: "Net Income",
      dataIndex: "netProfit",
      key: "netProfit",
      valueType: "digit",
      search: false,
      align: "center",
      sorter: true,
    },
    {
      title: "EPF",
      dataIndex: ["contribution", "epf", "employee"],
      key: "contribution.epf.employee",
      valueType: "digit",
      search: false,
      align: "center",
      sorter: true,
    },
    {
      title: "SOCSO",
      dataIndex: ["contribution", "socso", "total"],
      key: "contribution.socso.total",
      valueType: "digit",
      search: false,
      align: "center",
      sorter: true,
    },
    {
      title: "EIS",
      dataIndex: ["contribution", "eis", "total"],
      key: "contribution.eis.total",
      valueType: "digit",
      search: false,
      align: "center",
      sorter: true,
    },
    {
      title: "PCB",
      dataIndex: "pcb",
      key: "pcb",
      valueType: "digit",
      search: false,
      align: "center",
      sorter: true,
    },
    {
      title: "Attachment",
      dataIndex: "fileUrl",
      key: "fileUrl",
      valueType: "text",
      search: false,
      align: "center",
      render: (_, record) => {
        const handleFileDownload = async (fileUrl: string) => {
          try {
            await downloadPayslipFileFromS3(record.id, fileUrl);
          } catch (error) {
            message.error("Failed to open the file");
          }
        };

        return record.payslipUrl ? (
          <Button
            type="link"
            onClick={() => handleFileDownload(record.payslipUrl!)}
          >
            Open File
          </Button>
        ) : (
          <span>-</span>
        );
      },
    },
    ...(role === "admin" || role === "branchAdmin"
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            search: false,
            align: "center" as const,
            render: (_: any, record: IPayslipResponse) => {
              return (
                <EditOutlined
                  style={{ color: "blue" }}
                  onClick={() =>
                    navigate(`/payslips/${record.id}/edit`, {
                      state: {
                        payslipId: record.id,
                        displayName: record.user.name,
                      },
                    })
                  }
                />
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <ProTable<IPayslipResponse>
        cardBordered
        form={{ layout: "vertical" }}
        scroll={{ x: "max-content" }}
        request={async (params, sort, filter) => {
          try {
            params = removeEmptyParams(params);
            if (role === "admin" || role === "branchAdmin") {
              const data = await getPayslips({ filter, sort, params });
              const payslips = data.results;

              return {
                data: payslips,
                success: true,
                total: data.totalResults,
              };
            } else {
              const data = await getUserPayslips(user.id, {
                filter,
                sort,
                params,
              });
              const payslips = data.results;

              return {
                data: payslips,
                success: true,
                total: data.totalResults,
              };
            }
          } catch (error) {
            return {
              data: [],
              success: false,
              total: 10,
            };
          }
        }}
        rowKey={(data) => data.id}
        columns={columns}
        editable={{
          type: "multiple",
          onSave: async (_rowKey, _data, _row) => {
            await waitTime(2000);
          },
        }}
        actionRef={actionRef}
      />
    </>
  );
};
